import {
  shallow,
  useOthersMapped,
  useUpdateMyPresence,
} from "@liveblocks/react";
import { useAppDispatch, useAppSelector } from "@store";
import { setPeerGeneratingPreview } from "@store/story";
import { getIsSaving } from "@store/story/selectors";
import { useEffect } from "react";

// Mounted INSIDE RoomProvider (collab only). Two jobs:
//  1. Broadcast the local user's regeneration window (isSaving) via presence.
//  2. Aggregate peers' previewGenerating into a runtime Redux flag that the
//     (always-mounted, Liveblocks-free) header triggers can read everywhere.
export const PreviewPresenceBridge = () => {
  const isSaving = useAppSelector(getIsSaving);
  const updateMyPresence = useUpdateMyPresence();
  const dispatch = useAppDispatch();

  const peerGenerating = useOthersMapped(
    (other) => Boolean(other.presence.previewGenerating),
    shallow,
  );

  useEffect(() => {
    try {
      updateMyPresence({ previewGenerating: isSaving });
    } catch (error) {
      // The Liveblocks room proxy can be revoked mid-flush during a
      // RoomProvider teardown/reconnect (or a dev StrictMode remount), making
      // this synchronous presence write throw "Cannot perform 'get' on a proxy
      // that has been revoked". Presence is ephemeral and fire-and-forget, so
      // swallow it — letting it bubble unmounts the whole editor via the
      // ErrorBoundary. Matches the guard in UserPresenceProvider.
      console.warn("Failed to update preview presence:", error);
    }
  }, [isSaving, updateMyPresence]);

  useEffect(() => {
    const anyPeer = peerGenerating.some(([, generating]) => generating);
    dispatch(setPeerGeneratingPreview(anyPeer));
  }, [peerGenerating, dispatch]);

  useEffect(() => {
    return () => {
      // Leave a clean slate when collaboration unmounts.
      dispatch(setPeerGeneratingPreview(false));
    };
  }, [dispatch]);

  return null;
};
